import {Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {WidgetTableColumn} from '../../../models/widget-table-column';
import {DatePipe, NgClass} from '@angular/common';
import {PhoneNumberPipe} from '../../../../../shared/pipes/phone-number.pipe';
import {MatButtonModule} from '@angular/material/button';
import {WidgetAction} from '../../../models/widget-action';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule, MatIconRegistry} from '@angular/material/icon';
import {EmailContent} from '../../widget-content.model';
import {Router} from '@angular/router';
import {EMAIL_TEMPLATES, EmailTemplateType} from '../../../../../shared/services/content/email-templates.const';
import {MatChipsModule} from '@angular/material/chips';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {ConstantPrettyPipe} from '../../../../../shared/pipes/constant-pretty.pipe';
import {FilterType} from '../../../models/filter-type';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {environment} from '../../../../../../environments/environment';
import {DashboardPersistenceService} from '../../../../../shared/services/dashboard-persistence.service';
import {WidgetTable} from '../../../models/widget-table';
import {SnackbarService} from '../../../../../shared/services/snackbar.service';
import {CellStyleRuleService} from '../../services/cell-style-rule.service';
import {WidgetCellStyle} from '../../../models/widget-cell-style-rule';
import { ThemedImagesService } from '../../../../../shared/services/themed-images/themed-images.service';
import {ConfirmationDialogComponent} from '../../../../../shared/components/confirmation-dialog/confirmation-dialog.component';

type DynamicCellValue = string | number | Date | null | undefined;

interface FilterValue {
  fieldName: string;
  fieldValue: string;
}

interface FilterDateRange {
  fieldName: string;
  fieldValue: string;
  startDate: string;
  endDate: string;
  showAll: boolean;
}

@Component({
  selector: 'via-dynamic-material-table',
  imports: [MatTableModule, MatButtonModule, MatIconModule, MatChipsModule, MatSortModule, DatePipe, PhoneNumberPipe, ConstantPrettyPipe, NgClass],
  templateUrl: './dynamic-material-table.component.html',
  styleUrl: './dynamic-material-table.component.scss',
})
export class DynamicMaterialTableComponent implements OnInit {

  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly dashboardPersistenceService = inject(DashboardPersistenceService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly themedImages = inject(ThemedImagesService);
  private readonly cellStyleRuleService = inject(CellStyleRuleService);
  private readonly matDialog = inject(MatDialog);

  private readonly router = inject(Router);
  private _data: object[];
  private _columns: WidgetTableColumn[];
  private filterType: FilterType | undefined;
  private widgetTableKey: string;
  private primaryKeyField: string;

  private readonly valueFilterPredicate = (data: object, filter: string) => {
    const filterValue = JSON.parse(filter) as FilterValue;
    if (filterValue.fieldValue === 'ALL') {
      return true;
    } else {
      const fieldValue = (data as Record<string, DynamicCellValue>)[
        filterValue.fieldName
      ];

      return (
        String(fieldValue ?? '').toUpperCase() ===
        filterValue.fieldValue.toUpperCase()
      );
    }
  };

  private readonly dateRangeFilterPredicate = (data: object, filter: string)=> {
    const filterDateRange = JSON.parse(filter) as FilterDateRange;
    if (filterDateRange.showAll) {
      return true;
    } else {
      // @ts-expect-error We need to use this syntax because the structure of the data is dynamic
      let fieldValue = data[filterDateRange.fieldName] as string;
      fieldValue = fieldValue ? fieldValue.substring(0, 10) : fieldValue;
      return fieldValue >= filterDateRange.startDate && fieldValue <= filterDateRange.endDate;
    }
  };

  private readonly sortingDataAccessor = (data: object, sortHeaderId: string): string | number => {
    const value = sortHeaderId
      .split('.')
      .reduce<unknown>((obj, key) => {
        if (obj && typeof obj === 'object') {
          return (obj as Record<string, unknown>)[key];
        }

        return undefined;
      }, data);

    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value.trim().toLocaleLowerCase();
    }

    if (typeof value === 'number') {
      return value;
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    return String(value).trim().toLocaleLowerCase();
  };

  @Input() widgetKey: string;

  @Input() set data(value: object[]) {
    this.filterType = this.getFilterColumn()?.filter?.filterType;
    this._data = value;
    this.setupDatasource();
    this.dataLoaded = value?.length >= 0;
    this.showEmptyState = value?.length === 0;
  };

  @Input() set widgetTable(value: WidgetTable) {
    this._columns = value.columns;
    this.orderedColumns = value.columns.sort((a, b) => a.orderIndex - b.orderIndex);
    this.widgetTableKey = value.key;
    this.primaryKeyField = value.primaryKey;
  }

  @Input() showErrorState = false;

  @ViewChild(MatSort) sort: MatSort | undefined;
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setupDatasource();
  }

  orderedColumns: WidgetTableColumn[];
  dataSource: MatTableDataSource<object>;
  displayedColumns: string[] = [];
  dataLoaded = false;
  showEmptyState = false;
  selectedFilter: FilterValue | FilterDateRange | undefined;
  themedSvgHtml: SafeHtml = this.domSanitizer.bypassSecurityTrustHtml(
    this.themedImages.themedImage('error'),
  );

  constructor() {
    this.matIconRegistry.addSvgIcon('errorState', this.domSanitizer.bypassSecurityTrustResourceUrl(`${environment.appBaseUrl}/icon/error.svg`));
  }

  ngOnInit() {
    this.displayedColumns = this.orderedColumns.map((column: WidgetTableColumn) => this.getColumnName(column));
    this.setupDatasource();
  }

  get availableFilters(): string[] {
    const filteredColumn = this.getFilterColumn();
    if (filteredColumn) {
      return filteredColumn.filter?.filterValues ?? [];
    } else {
      return [];
    }
  }

  get defaultSortColumn(): string {
    const defaultSortColumn = this.orderedColumns.filter((column: WidgetTableColumn) => column.defaultSortColumn);
    if (defaultSortColumn && defaultSortColumn.length > 0) {
      return defaultSortColumn[0].dataField as string;
    } else {
      return this.orderedColumns[0].dataField ?? '';
    }
  }

  getColumnName(column: WidgetTableColumn): string {
    return column.dataField ? column.dataField : column.header.replace(' ', '');
  }

  getColumnClass(column: WidgetTableColumn): string {
    return `column-type-${column.type.toLowerCase().replaceAll('_', '-')}`;
  }

  getCellValue(row: object, column: WidgetTableColumn): DynamicCellValue {
    if (!column.dataField) {
      return undefined;
    }

    return (row as Record<string, DynamicCellValue>)[column.dataField];
  }

  getCellStyle(row: object, column: WidgetTableColumn): WidgetCellStyle | null {
    return this.cellStyleRuleService.getCellStyle(this.getCellValue(row, column), column);
  }

  isFilterSelected(filter: string): boolean {
    if (this.selectedFilter) {
      return 'fieldValue' in this.selectedFilter ? this.selectedFilter.fieldValue === filter : false;
    } else {
      return filter === 'ALL';
    }
  }

  toggleFilter(filter: string): void {
    this.selectedFilter = undefined;
    if (this.filterType === 'VALUE') {
      this.selectedFilter = {
        fieldName: this.getFilterColumn()?.dataField,
        fieldValue: filter
      } as FilterValue;
    } else {
      this.selectedFilter = this.setupDateRangeFilter(filter);
    }
    this.dataSource.filter = JSON.stringify(this.selectedFilter);
  }

  executeAction(action: WidgetAction, record: object) {
    if (!action) return;
    switch (action.actionType) {
      case 'LINK':
        this.navigate(action.linkTemplate, record);
        break;
      case 'EMAIL':
        this.sendEmail(action, record);
        break;
      case 'REMOVE':
        this.confirmRemoveItem(record);
    }
  }

  navigate(linkTemplate: string | undefined, data: object): void {
    if (linkTemplate) {
      let path = linkTemplate;
      let parameterStartIndex = path.indexOf('{');
      let parameterEndIndex = path.indexOf('}');
      let parameter: string;
      while (parameterStartIndex >= 0) {
        parameter = path.substring(parameterStartIndex + 1, parameterEndIndex);
        const parameterValue = (data as Record<string, DynamicCellValue>)[parameter];
        path = path.replace(`{${parameter}}`, String(parameterValue ?? ''));
        parameterStartIndex = path.indexOf('{');
        parameterEndIndex = path.indexOf('}');
      }
      this.router.navigateByUrl(path);
    }
  }

  sendEmail(action: WidgetAction, data: object): void {
    const emailTemplate = EmailTemplateType[action.emailTemplate as keyof typeof EmailTemplateType];
    const template: EmailContent | undefined = EMAIL_TEMPLATES.get(emailTemplate);

    if (!template) {
      console.warn('Email template not found:', emailTemplate);
      return;
    }
    const subject = template?.subject || '';
    const emailAddress = String((data as Record<string, DynamicCellValue>)['emailAddress'] ?? '');
    const body = template?.body ? template.body.replace('<<name>>', emailAddress) : '';

    const mailto = `mailto:${encodeURIComponent(emailAddress)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }

  private removeItem(data: object): void {
    // @ts-expect-error We are assuming that the data object will contain a field matching the primaryKeyField - Following the convention-over-configuration principle.
    this.dashboardPersistenceService.removeWidgetItem('MAIN', this.widgetKey, this.widgetTableKey, data[this.primaryKeyField]).subscribe({
      next: () => {
        const index = this._data.findIndex((row) => JSON.stringify(row) === JSON.stringify(data));
        this._data.splice(index, 1);
        this.dataSource = new MatTableDataSource(this._data);
      },
      error: () => {
        this.snackbarService.notifyError('Remove item failed');
      }
    })
  }

  private confirmRemoveItem(data: object): void {
    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      width: 'min(480px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'via-confirmation-dialog-panel',
      data: {
        title: 'Remove item',
        message: 'This action will remove the selected item from the list.',
        question: 'Are you sure?',
        cancelLabel: 'Cancel',
        confirmLabel: 'Confirm',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean | undefined) => {
      if (confirmed) {
        this.removeItem(data);
      }
    });
  }

  private setupDateRangeFilter(filter: string): FilterDateRange {
    const now = new Date();
    // Normalize to local midnight to avoid time drift
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate: Date | undefined;
    let endDate: Date | undefined;
    let showAll = false;
    let adjustedDay;
    switch (filter) {
      case 'ALL':
        showAll = true;
        break;
      case 'TODAY':
        startDate = today;
        endDate = today;
        break;
      case 'THIS_WEEK':
        // Convert Sunday (0) -> 7 to make Monday (1) the start
        adjustedDay = today.getDay() === 0 ? 7 : today.getDay();
        // Monday = start of the week
        startDate = new Date(today);
        startDate.setDate(today.getDate() - (adjustedDay - 1));

        // Sunday = end of the week
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;

      case 'THIS_MONTH':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);

        // Last day of month: day 0 of next month
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
    }

    return this.selectedFilter = {
        fieldName: this.getFilterColumn()?.dataField,
        fieldValue: filter,
        startDate: startDate ? this.formatDate(startDate) : undefined,
        endDate: endDate ? this.formatDate(endDate) : undefined,
        showAll: showAll
      } as FilterDateRange;
  }

  private formatDate(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  }

  private setupDatasource() {
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource(this._data);
      this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
      if (this.filterType === 'VALUE') {
        this.dataSource.filterPredicate = this.valueFilterPredicate;
      }
      else if (this.filterType === 'DATE_RANGE') {
        this.dataSource.filterPredicate = this.dateRangeFilterPredicate;
      }
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.sort.disableClear = true;
    }
  }

  private getFilterColumn(): WidgetTableColumn | undefined {
    const filteredColumns = this._columns?.filter((column: WidgetTableColumn) => !!column.filter);
    return filteredColumns ? filteredColumns[0] : undefined;
  }

}
